'use client';
import { memo, useMemo, useState, useEffect } from 'react';
import { InputTextProps } from '../input-text';
import { InputText } from '../input-text';
import { NumberFormatter } from './helper';

export type InputNumberProps = {
  decimalScale?: number;
  isCurrency?: boolean;
  currency?: string;
  isPercent?: boolean;
  onChange?: (value: number | undefined) => void;
} & InputTextProps;

export const InputNumber = memo<InputNumberProps>(function InputNumber({
  isCurrency,
  isPercent,
  currency = 'BRL',
  decimalScale = 2,
  value: externalValue,
  onChange: externalOnChange,
  onBlur,
  ...props
}) {
  const [value, setValue] = useState<number | undefined>(
    typeof externalValue === 'number' ? externalValue : undefined
  );

  useEffect(() => {
    if (externalValue !== value) {
      setValue(typeof externalValue === 'number' ? externalValue : undefined);
    }
  }, [externalValue]);

  const formattedValue = useMemo(() => {
    if (value === undefined || value === null) return '';

    if (isCurrency) {
      return NumberFormatter.formatCurrency(value, currency, decimalScale);
    }

    return NumberFormatter.formatDecimal(value, decimalScale);
  }, [value, isCurrency, isPercent, currency, decimalScale]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let rawValue = event.target.value.replace(/[^\d]/g, '');

    if (!rawValue) {
      setValue(undefined);
      externalOnChange?.(undefined as any);
      return;
    }

    const numberValue = parseInt(rawValue, 10) / Math.pow(10, decimalScale);
    setValue(numberValue);
    externalOnChange?.(numberValue as any);
  };

  const handleBlur = () => {
    onBlur?.(value as any);
  };

  return (
    <InputText
      {...props}
      type="text"
      inputMode="numeric"
      onChange={handleChange}
      value={formattedValue}
      InputProps={{
        ...props?.InputProps,
        onBlur: handleBlur,
        endAdornment:
          props?.InputProps?.endAdornment ?? (isPercent ? '%' : undefined),
      }}
    />
  );
});
